import React from "react";
import { Link } from "react-router-dom";
import {
  FlaskConical,
  Heart,
  Sparkles,
  Sparkle,
  Star,
  Moon,
  Wand2,
  User,
  ArrowUpRight,
} from "lucide-react";

// A handful of twinkling "stars" scattered across the hero backdrop.
const STARS = [
  { top: "12%", left: "8%", size: 3, delay: "0s" },
  { top: "22%", left: "82%", size: 2, delay: "0.6s" },
  { top: "70%", left: "15%", size: 2, delay: "1.2s" },
  { top: "78%", left: "72%", size: 3, delay: "0.3s" },
  { top: "35%", left: "48%", size: 2, delay: "1.8s" },
  { top: "8%", left: "60%", size: 2, delay: "2.4s" },
  { top: "58%", left: "90%", size: 3, delay: "0.9s" },
  { top: "88%", left: "40%", size: 2, delay: "1.5s" },
  { top: "44%", left: "5%", size: 2, delay: "2.1s" },
];

// Big whimsical doodles that lazily drift around the hero backdrop.
const DOODLES = [
  { Icon: Sparkles, top: "12%", left: "9%", size: 72, opacity: 0.2, delay: "0s" },
  { Icon: Star, top: "20%", left: "82%", size: 52, opacity: 0.16, delay: "1.4s" },
  { Icon: Moon, top: "62%", left: "87%", size: 84, opacity: 0.14, delay: "0.7s" },
  { Icon: Star, top: "74%", left: "7%", size: 60, opacity: 0.18, delay: "2.2s" },
  { Icon: Sparkle, top: "46%", left: "93%", size: 40, opacity: 0.16, delay: "1.0s" },
  { Icon: Sparkle, top: "84%", left: "44%", size: 46, opacity: 0.14, delay: "1.8s" },
  { Icon: Moon, top: "8%", left: "54%", size: 36, opacity: 0.12, delay: "2.6s" },
];

// Gentle per-card tilts and offsets so the row feels hand-arranged.
const TILTS = [
  "-rotate-3 sm:translate-y-2",
  "rotate-2 sm:-translate-y-3",
  "-rotate-1 sm:translate-y-4",
];

const DESTINATIONS = [
  {
    to: "/about",
    title: "About",
    blurb: "Who's behind the daydreams.",
    Icon: User,
  },
  {
    to: "/interests",
    title: "Interests",
    blurb: "Tech, board games, live music, and time travel.",
    Icon: Heart,
  },
  {
    to: "/sillyprojects",
    title: "Silly Projects",
    blurb: "Whatever my imagination dreams up.",
    Icon: FlaskConical,
  },
];

const Home = () => {
  return (
    <div className="relative min-h-[calc(100vh-4rem)] overflow-hidden bg-dreamy">
      {/* Aurora glows — gold dusk meets dark-blue night. */}
      <div
        aria-hidden
        className="pointer-events-none absolute -left-32 -top-32 h-96 w-96 rounded-full bg-primary/20 blur-3xl animate-aurora"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 -right-24 h-[28rem] w-[28rem] rounded-full bg-[hsl(var(--blue)/0.25)] blur-3xl animate-aurora"
        style={{ animationDelay: "-7s" }}
      />

      {/* Twinkling stardust. */}
      {STARS.map((s, i) => (
        <span
          key={i}
          aria-hidden
          className="pointer-events-none absolute rounded-full bg-primary animate-twinkle"
          style={{
            top: s.top,
            left: s.left,
            width: s.size,
            height: s.size,
            animationDelay: s.delay,
          }}
        />
      ))}

      {/* Drifting whimsical doodles. */}
      {DOODLES.map(({ Icon, top, left, size, opacity, delay }, i) => (
        <Icon
          key={`d-${i}`}
          aria-hidden
          className="pointer-events-none absolute text-primary animate-drift"
          style={{ top, left, width: size, height: size, opacity, animationDelay: delay }}
        />
      ))}

      <div className="container relative mx-auto flex min-h-[calc(100vh-4rem)] flex-col items-center justify-center px-4 py-20 text-center">
        {/* Whimsical eyebrow. */}
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-card/60 px-4 py-1.5 text-xs font-medium text-primary backdrop-blur">
          <Wand2 className="h-3.5 w-3.5" />
          <span>a little corner of the internet, spun from imagination</span>
        </div>

        <div className="relative inline-block">
          <h1 className="text-balance text-3xl font-bold tracking-tight sm:text-5xl">
            <span className="text-gold-shimmer">Keyoni McNair</span>
          </h1>
          {/* Hand-drawn gold squiggle that draws itself in under the name. */}
          <svg
            aria-hidden
            viewBox="0 0 300 12"
            preserveAspectRatio="none"
            className="absolute -bottom-3 left-0 h-3 w-full overflow-visible"
          >
            <path
              d="M3 7 Q 40 1, 78 7 T 152 7 T 226 7 T 297 6"
              fill="none"
              stroke="hsl(var(--gold))"
              strokeWidth="3"
              strokeLinecap="round"
              className="animate-draw"
            />
          </svg>
        </div>

        <p className="mt-6 max-w-xl text-pretty text-lg text-muted-foreground sm:text-xl">
          Welcome to my little daydream of a website — a place where
          curiosity wanders, ideas get to be{" "}
          <span className="font-medium text-foreground">gloriously silly</span>,
          and{" "}
          <span className="inline-flex items-center gap-1 font-medium text-primary">
            imagination runs the show
            <Sparkles className="h-4 w-4" />
          </span>
          .
        </p>

        {/* Destination cards — loosely tilted, like pinned notes. */}
        <div className="mt-16 grid w-full max-w-3xl gap-5 sm:grid-cols-3">
          {DESTINATIONS.map(({ to, title, blurb, Icon }, i) => (
            <Link
              key={to}
              to={to}
              className={`group relative flex flex-col items-start gap-3 overflow-hidden rounded-2xl border border-border bg-card/70 p-5 text-left backdrop-blur transition-all duration-300 hover:rotate-0 hover:-translate-y-1.5 hover:border-primary/60 hover:glow-gold ${TILTS[i]}`}
            >
              <div className="flex w-full items-center justify-between">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground group-hover:animate-wiggle">
                  <Icon className="h-5 w-5" />
                </span>
                <ArrowUpRight className="h-4 w-4 text-muted-foreground transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-primary" />
              </div>
              <div>
                <h2 className="font-semibold text-card-foreground">{title}</h2>
                <p className="mt-0.5 text-sm text-muted-foreground">{blurb}</p>
              </div>
            </Link>
          ))}
        </div>

        <p className="mt-12 inline-flex items-center gap-1.5 text-sm text-muted-foreground/80">
          <Star className="h-3.5 w-3.5 text-primary" />
          forever a work of wonder — wander back anytime for something new
        </p>
      </div>
    </div>
  );
};

export default Home;
